import { Injectable } from '@angular/core';
import { switchMap, map, tap } from 'rxjs/operators';
import { OverlayService } from '@services/overlay.service';
import {
  DepartmentType,
  GqlService,
  RoleType,
  updateUserPermissionMutation,
  UserTrialInput,
} from '@services/gql.service';

import { TrialUserStore, UserModel } from './trial-user.store';
import { OrganizationQuery } from '../organization/organization.query';
import { MainQuery } from '../../layouts/main-layout/state/main.query';
import { batchPromises } from '@utils/batch-promises';

const mapPermissionArrToObj = (
  arr: {
    permission_id: string;
    permission_roles: 'E' | 'U';
  }[]
) => {
  const obj: { [k: string]: 'E' | 'U' } = {};
  arr.forEach((x) => {
    obj[x.permission_id] = x.permission_roles;
  });
  return obj;
};

@Injectable({ providedIn: 'root' })
export class TrialUserService {
  constructor(
    private trialUserStore: TrialUserStore,
    private vendorsQuery: OrganizationQuery,
    private mainQuery: MainQuery,
    private overlayService: OverlayService,
    private gqlService: GqlService
  ) {}

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.trialUserStore.setLoading(true);

        return this.gqlService.listUsers$(false).pipe(
          tap((x) => {
            if (x.data) {
              const newData = x.data.map((y) => {
                const arr = (y.permissions as unknown) as any;
                const permissions = mapPermissionArrToObj(arr);
                return {
                  ...y,
                  permissions,
                };
              });
              this.trialUserStore.set(newData);
            }
            this.trialUserStore.setLoading(false);
          })
        );
      })
    );
  }

  listUserNamesWithEmail() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.trialUserStore.setLoading(true);

        return this.gqlService.listUserNamesWithEmail$(true).pipe(
          map((x) => {
            return { ...x, data: x.data ? x.data : null };
          }),
          tap(() => {
            this.trialUserStore.setLoading(false);
          })
        );
      })
    );
  }

  async add({
    email,
    family_name,
    given_name,
    department,
    organization_id,
    title,
    trial_id,
    role,
  }: {
    given_name: string;
    family_name: string;
    email: string;
    title: string;
    organization_id: string;
    trial_id: string;
    department: DepartmentType[];
    role: RoleType;
  }) {
    this.trialUserStore.setLoading(true);

    const { data, errors, success } = await this.gqlService
      .createUser$({
        given_name,
        family_name,
        email,
        title,
      })
      .toPromise();

    if (success && data) {
      const { data: data2, errors: trialErrors, success: trialSuccess } = await this.addToTrial({
        trial_id,
        user_id: data.sub,
        department,
        organization_id,
        given_name,
        family_name,
        title,
        role,
      });
      if (trialSuccess && data2) {
        this.trialUserStore.add({
          __typename: 'User',
          email,
          family_name,
          given_name,
          organization: data2.organization,
          department: data2.department,
          sub: data.sub,
          title,
          permissions: mapPermissionArrToObj(
            data2.permissions as {
              permission_id: string;
              permission_roles: 'E' | 'U';
            }[]
          ),
          role: data2.role,
        });
        this.trialUserStore.setLoading(false);

        return true;
      }
      // eslint-disable-next-line no-alert
      alert(trialErrors);
    } else {
      // eslint-disable-next-line no-alert
      alert(errors);
    }

    this.trialUserStore.setLoading(false);
    return false;
  }

  async reinvite({
    email,
    family_name,
    given_name,
    title,
  }: {
    given_name: string;
    family_name: string;
    email: string;
    title: string;
  }) {
    this.trialUserStore.setLoading(true);

    const { data, errors, success } = await this.gqlService
      .createUser$({
        given_name,
        family_name,
        email,
        title,
      })
      .toPromise();

    if (success && data) {
      this.overlayService.success('User successfully reinvited');
    } else {
      this.overlayService.error(errors);
    }

    this.trialUserStore.setLoading(false);
    return false;
  }

  async addToTrial({
    organization_id,
    given_name,
    family_name,
    title,
    department,
    role,
    trial_id,
    user_id,
  }: UserTrialInput) {
    return this.gqlService
      .addUserToTrial$({
        trial_id,
        department,
        user_id,
        organization_id,
        given_name,
        family_name,
        title,
        role,
      })
      .toPromise();
  }

  async update({
    email,
    sub,
    title,
    given_name,
    department,
    family_name,
    role,
    organization_id,
  }: Omit<UserTrialInput, 'user_id' | 'trial_id'> & { sub: string; email: string }) {
    const { success, errors, data } = await this.addToTrial({
      organization_id,
      given_name,
      department,
      family_name,
      title,
      role,
      trial_id: this.mainQuery.getValue().trialKey,
      user_id: sub as string,
    });

    if (success && data) {
      this.trialUserStore.update(sub, {
        role,
        sub,
        family_name,
        given_name,
        department: data.department,
        organization: data.organization,
        title,
        email,
      });
    } else {
      this.overlayService.error(errors);
    }

    return { success, errors, data };
  }

  async updatePermission(user: UserModel, permission_id: string, role: 'E' | 'U') {
    this.trialUserStore.setLoading(true);
    const trial_id = this.mainQuery.getValue().trialKey;
    const { success } = await this.gqlService
      .updateUserPermission$({
        trial_id,
        user_id: user.sub || '',
        permission_id,
        permission_roles: role,
      })
      .toPromise();

    if (success) {
      const perms = { ...user.permissions, ...{ [permission_id]: role } };
      this.trialUserStore.update(user.sub, { permissions: perms });
    }

    this.trialUserStore.setLoading(false);

    return success;
  }

  async updateGroupPermission(user: UserModel, permissions: any, role: 'E' | 'U') {
    const promises: Promise<GraphqlResponse<updateUserPermissionMutation>>[] = [];
    this.trialUserStore.setLoading(true);
    const trial_id = this.mainQuery.getValue().trialKey;
    permissions.forEach((x: any) => {
      promises.push(
        this.gqlService
          .updateUserPermission$({
            trial_id,
            user_id: user.sub || '',
            permission_id: x.id,
            permission_roles: role,
          })
          .toPromise()
      );
    });

    const responses = await batchPromises(promises, (p) => p);

    if (responses.every((r) => !(r instanceof Error) && r?.success)) {
      const perms = { ...user.permissions };
      responses.forEach((resp) => {
        perms[(resp as GraphqlResponse<updateUserPermissionMutation>).data!.permission_id] = role;
      });
      this.trialUserStore.update(user.sub, { permissions: perms });
    }

    this.trialUserStore.setLoading(false);
  }

  async remove(user_id: string) {
    const { success, errors } = await this.gqlService.removeUserFromTrial$(user_id).toPromise();

    if (success) {
      this.trialUserStore.remove(user_id);
      this.overlayService.success('User successfully removed');
    } else {
      this.overlayService.error(['User could not be removed', ...errors]);
    }

    return { success, errors };
  }
}
